import { ApolloLink } from '@apollo/client';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { SubscriptionClient } from 'subscriptions-transport-ws';

import LoggingService from 'components/core/logging/LoggingService';
import { APIType, getGraphURL } from 'utils/apiUtils';
import { authManager } from 'utils/authUtils';

import { getDefaultHeaders } from 'store/api/ApiRequest';

const subscriptionUrl = getGraphURL(APIType.SUBSCRIPTION);

/**
 * Subscription client for WebSocket connections.
 * Returns null if no subscription URL is configured (e.g., in Storybook builds).
 */
export const subscriptionClient = subscriptionUrl
  ? new SubscriptionClient(subscriptionUrl, {
      connectionParams: () => ({ ...getDefaultHeaders() }),
      reconnect: true,
      // Type response `any` because the types file for this is incorrect
      connectionCallback: (response: any) => {
        if (!response?.errors?.find(error => error.extensions?.status === 401)) {
          return;
        }

        void authManager.logout();
        LoggingService.clearUser();
      },
    })
  : null;

/**
 * WebSocket link for GraphQL subscriptions.
 * Returns an empty link if no subscription client is available.
 */
export const wsLink = subscriptionClient ? new WebSocketLink(subscriptionClient) : new ApolloLink();

/**
 * SplitLink - Determines which link is returned (http or websocket)
 *
 * The function takes three parameters:
 * - A function that's called for each operation to execute
 * - The Link to use for an operation if the function returns a "truthy" value
 * - The Link to use for an operation if the function returns a "falsy" value
 */
export const createSplitLink = (wsLink: ApolloLink | WebSocketLink, httpLink: any) =>
  ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);

      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink
  );
